// Angular
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
// RxJS
import { Observable, BehaviorSubject, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { HelperService } from './helper.service';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

const API_OVERVIEW_URL = environment.baseUrl + '/overview';

export enum LowStockEmailAlertType {
	AlertOnlyProductLowStock = 'AlertOnlyProductLowStock',
	FavoriteStoreLowStock = 'FavoriteStoreLowStock'
}

export interface LowStockEmailAlert {
	alertType: LowStockEmailAlertType;
	productId: number;
	favoriteStoreId?: number;
	sku: string;
	thresholdLevel: number;
	currentQuantity: number;
	sentAt?: string;
}

export interface LowStockEmailAlertDismissRequest {
	alertType?: LowStockEmailAlertType;
	favoriteStoreId?: number;
}

@Injectable({ providedIn: 'root' })
export class OverviewService {
	constructor(private http: HttpClient,
		private helperService: HelperService,
		private toastr: ToastrService,
		private translate: TranslateService) { }

	getDashboard(periods: string, compare: string, storeId?: number, customFrom?: string, customTo?: string, includeChart = true, search?: string, productIds?: number[]): Observable<any> {
		let params: any = { periods, compare };
		if (storeId != null) {
			params.storeId = storeId;
		}
		if (customFrom && customTo) {
			params.customFrom = customFrom;
			params.customTo = customTo;
		}
		// The per-tile dashboard never renders the 30-day chart; skipping it lets the backend pull
		// only each tile's own date range instead of a full month of daily rows.
		if (!includeChart) {
			params.includeChart = false;
		}
		if (search && search.trim()) {
			params.search = search.trim();
		}
		// Explicit product selection (dashboard row checkboxes); repeated query keys bind to List<int>.
		if (productIds && productIds.length) {
			params.productIds = productIds;
		}
		const url = `${API_OVERVIEW_URL}/Dashboard`;
		return this.http.get<any>(url, { params, headers: this.helperService.getHTTPHeaders() })
			.pipe(catchError(this.handleError('getDashboard')));
	}

	/** Full P&L breakdown for a single product over a date range (for the per-product "More" popup). */
	getProductBreakdown(productId: number, from: string, to: string, storeId?: number): Observable<any> {
		let params: any = { productId, from, to };
		if (storeId != null) {
			params.storeId = storeId;
		}
		const url = `${API_OVERVIEW_URL}/ProductBreakdown`;
		return this.http.get<any>(url, { params, headers: this.helperService.getHTTPHeaders() })
			.pipe(catchError(this.handleError('getProductBreakdown')));
	}

	getSkusWithImproperInventory(): Observable<any> {
		const url = `${API_OVERVIEW_URL}/SkusWithStockZeroInventoryWarning`;
		return this.http.get<any>(url, { headers: this.helperService.getHTTPHeaders() })
			.pipe(catchError(this.handleError('SkusWithStockZeroInventoryWarning')));
	}

	getFixedStockLowStockAlerts(): Observable<LowStockEmailAlert[]> {
		const url = `${API_OVERVIEW_URL}/FixedStockLowStockAlerts`;
		return this.http.get<LowStockEmailAlert[]>(url, { headers: this.helperService.getHTTPHeaders() })
			.pipe(catchError(this.handleError<LowStockEmailAlert[]>('FixedStockLowStockAlerts')));
	}

	dismissFixedStockLowStockAlert(alert: LowStockEmailAlert): Observable<any> {
		const url = `${API_OVERVIEW_URL}/FixedStockLowStockAlerts/${alert.productId}/Dismiss`;
		const request: LowStockEmailAlertDismissRequest = {
			alertType: alert.alertType,
			favoriteStoreId: alert.favoriteStoreId
		};
		return this.http.post<any>(url, request, { headers: this.helperService.getHTTPHeaders() })
			.pipe(catchError(this.handleError<any>('DismissFixedStockLowStockAlert')));
	}

	getWalmartSellerStoresMissingRefreshToken(): Observable<string[]> {
		const url = `${API_OVERVIEW_URL}/WalmartSellerStoresMissingRefreshToken`;
		return this.http.get<string[]>(url, { headers: this.helperService.getHTTPHeaders() })
			.pipe(catchError(this.handleError<string[]>('WalmartSellerStoresMissingRefreshToken')));
	}

	getWalmartSellerStoresRefreshTokenExpiring(): Observable<string[]> {
		const url = `${API_OVERVIEW_URL}/WalmartSellerStoresRefreshTokenExpiring`;
		return this.http.get<string[]>(url, { headers: this.helperService.getHTTPHeaders() })
			.pipe(catchError(this.handleError<string[]>('WalmartSellerStoresRefreshTokenExpiring')));
	}

	orgSetup(): Observable<any> {
		return this.http.get(`${API_OVERVIEW_URL}/orgSetup`,
			{ headers: this.helperService.getHTTPHeaders() })
			.pipe(catchError(this.handleError('orgSetup')));
	}

	skipOrgSetup(): Observable<any> {
		return this.http.patch(`${API_OVERVIEW_URL}/skipOrganizationSetup`,
			{ headers: this.helperService.getHTTPHeaders() })
			.pipe(catchError(this.handleError('skipOrgSetup')));
	}

	private handleError<T>(operation = 'operation', result?: T) {
		return (error: any): Observable<T> => {
			switch (error.status) {
				case 401:
					window.location.href = '/login';
					break;
				case 422:
					this.toastr.error(this.translate.instant('PRODUCTS.MESSAGES.ERRORS.' + error.error.reason, error.error));
					break;
				default:
					this.toastr.error(this.translate.instant('GENERAL.MESSAGES.ERROR'));
			}
			return throwError(error);
		};
	}
}
